/**
 * NH Media Rail (2026-07)
 * =======================
 * Shared scroll-snap media carousel:
 *   - HJ piece page full-bleed gallery (sections/main-high-jewelry-product.liquid)
 *   - HJ salon editorial "image rail" blocks (sections/main-high-jewelry-collection.liquid)
 *
 * Markup contract:
 *   <nh-media-rail>
 *     <ul data-rail-track> <li data-rail-slide>…</li> … </ul>
 *     <div data-rail-controls hidden>
 *       <button data-rail-prev>…</button>
 *       <span data-rail-dots></span>   (dot buttons injected here)
 *       <button data-rail-next>…</button>
 *     </div>
 *   </nh-media-rail>
 *
 * Accessibility (WCAG 2.2 AA):
 *   - Progressive enhancement: without JS the track is a plain scrollable
 *     list; controls stay hidden until JS unhides them (no dead buttons).
 *   - Prev/next carry localized labels from data attributes; at either end
 *     they get aria-disabled (still focusable — no keyboard trap, 2.1.1).
 *   - Dots are real buttons, min 24px targets (2.5.8), labeled
 *     "Slide x of y" via data-label-slide; active dot gets aria-current.
 *   - Scrolling respects prefers-reduced-motion (2.3.3): instant jumps.
 *   - No autoplay/auto-advance, so 2.2.2 does not apply to the rail.
 */

class NHMediaRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    this.slides = Array.from(this.querySelectorAll('[data-rail-slide]'));
    this.controls = this.querySelector('[data-rail-controls]');
    this.prevBtn = this.querySelector('[data-rail-prev]');
    this.nextBtn = this.querySelector('[data-rail-next]');
    this.dotsWrap = this.querySelector('[data-rail-dots]');
    if (!this.track || this.slides.length < 2) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    /* Liquid passes the localized template with [[index]] / [[length]]
       placeholders — nested {{ }} inside a t: parameter is a Liquid
       syntax error on Shopify's GitHub-integration validator. */
    this.labelTpl = this.dataset.labelSlide || 'Slide [[index]] of [[length]]';

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.buildDots();
    this.observeSlides();
    this.track.addEventListener('scroll', () => this.queueEdgeUpdate(), { passive: true, signal });
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        if (this.prevBtn.getAttribute('aria-disabled') === 'true') return;
        this.step(-1);
      }, { signal });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        if (this.nextBtn.getAttribute('aria-disabled') === 'true') return;
        this.step(1);
      }, { signal });
    }
    this.updateEdges();

    // When every slide fits, center the track and drop the arrows and dots,
    // since there is nothing to scroll. Re-evaluated on resize.
    this.updateFit();
    this.fitObserver = new ResizeObserver(() => this.queueFitUpdate());
    this.fitObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.fitObserver?.disconnect();
    this.fitObserver = null;
    this.slideObserver?.disconnect();
    this.slideObserver = null;
    if (this.edgeRaf) cancelAnimationFrame(this.edgeRaf);
    if (this.fitRaf) cancelAnimationFrame(this.fitRaf);
    this.edgeRaf = null;
    this.fitRaf = null;
    if (this.dots) this.dots.forEach((dot) => dot.remove());
    this.dots = undefined;
  }

  queueFitUpdate() {
    if (this.fitRaf) return;
    this.fitRaf = requestAnimationFrame(() => {
      this.fitRaf = null;
      if (this.track) this.updateFit();
    });
  }

  updateFit() {
    const fits = this.track.scrollWidth <= this.track.clientWidth + 2;
    this.classList.toggle('nh-rail--fits', fits);
    if (this.controls) this.controls.hidden = fits;

    /* WCAG 2.1.1 Keyboard (axe scrollable-region-focusable) — the track
       scrolls horizontally but its slides hold only images, so there is
       nothing focusable inside it and a keyboard user cannot reach the
       scroll. Make the track itself a tab stop so the arrow keys scroll
       it, but only while it actually overflows: when every slide fits
       there is nothing to scroll and the tab stop would be dead weight.
       The track already carries an aria-label (the piece title), so it
       announces itself on focus. Do not use `inert` here — it kills
       pointer events as well as focus. */
    if (fits) {
      this.track.removeAttribute('tabindex');
    } else {
      this.track.setAttribute('tabindex', '0');
    }
  }

  slideLabel(i) {
    return this.labelTpl
      .replace('[[index]]', String(i + 1))
      .replace('[[length]]', String(this.slides.length));
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dots = this.slides.map((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'nh-rail__dot';
      dot.setAttribute('aria-label', this.slideLabel(i));
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsWrap.appendChild(dot);
      return dot;
    });
  }

  observeSlides() {
    this.current = 0;
    this.slideObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.current = this.slides.indexOf(entry.target);
          this.paintDots();
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((s) => this.slideObserver?.observe(s));
    this.paintDots();
  }

  paintDots() {
    if (!this.dots) return;
    this.dots.forEach((dot, i) => {
      if (i === this.current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  behavior() {
    return this.reduced.matches ? 'auto' : 'smooth';
  }

  goTo(i) {
    const slide = this.slides[i];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: this.behavior() });
  }

  step(dir) {
    const target = Math.min(Math.max(this.current + dir, 0), this.slides.length - 1);
    this.goTo(target);
  }

  queueEdgeUpdate() {
    if (this.edgeRaf) return;
    this.edgeRaf = requestAnimationFrame(() => {
      this.edgeRaf = null;
      this.updateEdges();
    });
  }

  updateEdges() {
    const max = this.track.scrollWidth - this.track.clientWidth - 1;
    if (this.prevBtn) this.prevBtn.setAttribute('aria-disabled', this.track.scrollLeft <= 1 ? 'true' : 'false');
    if (this.nextBtn) this.nextBtn.setAttribute('aria-disabled', this.track.scrollLeft >= max ? 'true' : 'false');
  }
}

if (!customElements.get('nh-media-rail')) {
  customElements.define('nh-media-rail', NHMediaRail);
}

/**
 * NH Editorial Video — ambient muted loop with a real pause control.
 *   - Autoplays ONLY when in view and only if the user does not prefer
 *     reduced motion (WCAG 2.2.2 Pause Stop Hide + 2.3.3).
 *   - The toggle button is aria-pressed and swaps its visually-hidden
 *     label between the localized play/pause strings (data attributes).
 *   - A user pause is sticky: the IntersectionObserver will not restart
 *     the video after a manual pause.
 */
class NHEditorialVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-video-toggle]');
    this.label = this.querySelector('[data-video-label]');
    if (!this.video) return;

    this.video.muted = true;
    this.video.setAttribute('playsinline', '');
    this.userPaused = false;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      this.video.autoplay = false;
      this.video.pause();
    } else {
      this.viewObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (this.userPaused) return;
            if (entry.isIntersecting) this.video.play().catch(() => {});
            else this.video.pause();
          });
        },
        { threshold: 0.25 }
      );
      this.viewObserver.observe(this.video);
    }

    if (this.toggle) {
      this.toggle.addEventListener(
        'click',
        () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.video.play().catch(() => {});
          } else {
            this.userPaused = true;
            this.video.pause();
          }
          this.paint();
        },
        { signal }
      );
      this.video.addEventListener('play', () => this.paint(), { signal });
      this.video.addEventListener('pause', () => this.paint(), { signal });
      this.paint();
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.viewObserver?.disconnect();
    this.viewObserver = null;
  }

  paint() {
    if (!this.toggle) return;
    const playing = !this.video.paused;
    this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    if (this.label) {
      this.label.textContent = playing
        ? this.toggle.dataset.labelPause || 'Pause video'
        : this.toggle.dataset.labelPlay || 'Play video';
    }
  }
}

if (!customElements.get('nh-editorial-video')) {
  customElements.define('nh-editorial-video', NHEditorialVideo);
}
